import type { Map as MapboxMap } from "mapbox-gl";
import type { AddressGridAggregationsResponse } from "../../api/map/types";

const SOURCE_ID = "address-aggregations";
const HEATMAP_LAYER_ID = "address-heatmap-layer";
const GRID_LABELS_LAYER_ID = "address-grid-labels";

/**
 * Ensures the address aggregations source and layers exist on the map (idempotent)
 */
export const ensureAddressAggregationsLayer = (map: MapboxMap): void => {
    try {
        // Create source if it doesn't exist
        if (!map.getSource(SOURCE_ID)) {
            map.addSource(SOURCE_ID, {
                type: "geojson",
                data: { type: "FeatureCollection", features: [] }
            });
        }

        // Remove existing layers if they exist (to update styling)
        if (map.getLayer("address-aggregations-circles")) {
            map.removeLayer("address-aggregations-circles");
        }
        if (map.getLayer(HEATMAP_LAYER_ID)) {
            map.removeLayer(HEATMAP_LAYER_ID);
        }
        if (map.getLayer(GRID_LABELS_LAYER_ID)) {
            map.removeLayer(GRID_LABELS_LAYER_ID);
        }
        // Legacy fill layer cleanup
        if (map.getLayer("address-aggregations-fill")) {
            map.removeLayer("address-aggregations-fill");
        }

        // Add circle layer for aggregation cells.
        // Backend sends Point geometry (cell centroid), so we use circles sized by address count.
        // Minimum radius 10px so cells with few addresses are still visible.
        map.addLayer({
            id: "address-aggregations-circles",
            type: "circle",
            source: SOURCE_ID,
            paint: {
                "circle-radius": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    8,  ["max", ["min", ["*", ["sqrt", ["get", "totalAddresses"]], 1.5], 30], 10],
                    10, ["max", ["min", ["*", ["sqrt", ["get", "totalAddresses"]], 2],   40], 12],
                    12, ["max", ["min", ["*", ["sqrt", ["get", "totalAddresses"]], 2.5], 50], 14]
                ],
                "circle-color": [
                    "interpolate",
                    ["linear"],
                    ["get", "progressPercent"],
                    0,   "#3b82f6", // Blue (pending)
                    50,  "#facc15", // Yellow (partial)
                    100, "#22c55e"  // Green (completed)
                ],
                "circle-opacity": 0.75,
                "circle-stroke-color": "#ffffff",
                "circle-stroke-width": 2,
                "circle-stroke-opacity": 0.9
            }
        });

        // Add heatmap layer (Phase 3.3: Heatmap Visualization) - subtle overlay
        map.addLayer({
            id: HEATMAP_LAYER_ID,
            type: "heatmap",
            source: SOURCE_ID,
            paint: {
                // Heatmap intensity based on delivery progress
                "heatmap-weight": [
                    "interpolate",
                    ["linear"],
                    ["get", "progressPercent"],
                    0,
                    0.3, // Low progress = light
                    100,
                    1 // High progress = intense
                ],
                "heatmap-color": [
                    "interpolate",
                    ["linear"],
                    ["heatmap-density"],
                    0,
                    "rgba(255, 255, 255, 0)",
                    0.2,
                    "rgba(255, 200, 100, 0.6)", // Yellow
                    0.5,
                    "rgba(255, 100, 50, 0.7)", // Orange
                    1,
                    "rgba(200, 50, 50, 0.8)" // Red
                ],
                "heatmap-radius": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    8,
                    30,
                    10,
                    50,
                    12,
                    80
                ],
                "heatmap-opacity": 0.4,
                "heatmap-intensity": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    8,
                    1.2,
                    12,
                    1.8
                ]
            }
        });

        // Add grid cell labels (showing completion stats)
        map.addLayer({
            id: GRID_LABELS_LAYER_ID,
            type: "symbol",
            source: SOURCE_ID,
            layout: {
                "text-field": [
                    "format",
                    ["get", "completedDeliveries"],
                    "",
                    "/",
                    ["get", "totalTargetDeliveries"],
                    { "font-scale": 0.8 }
                ],
                "text-size": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    8,
                    10,
                    12,
                    12
                ],
                "text-anchor": "center",
                "text-allow-overlap": false,
                "text-ignore-placement": false
            },
            paint: {
                "text-color": "#000000",
                "text-halo-color": "#ffffff",
                "text-halo-width": 2,
                "text-halo-blur": 1
            }
        });
    } catch (error) {
        // If layer creation fails (style not loaded), just return
        // The caller (updateAddressAggregationsLayer) will handle waiting for style.load
        console.warn("[AddressAggregationsLayer] Failed to create layer:", error);
    }
};

/**
 * Determines which precision to show based on zoom level
 * Returns a single precision to avoid overlapping aggregations
 */
function getVisiblePrecisionForZoom(zoom: number): number {
    // Show only one precision at a time to avoid overlapping aggregations
    if (zoom < 8) return 3;
    if (zoom < 10) return 4;
    if (zoom < 12) return 5;
    if (zoom < 14) return 6;
    if (zoom < 16) return 7;
    return 8;
}

/**
 * Updates the address aggregations layer with data from the API
 * Filters features by precision based on current zoom level
 * Shows only aggregations with precision <= max precision for current zoom
 */
export const updateAddressAggregationsLayer = (
    map: MapboxMap,
    data: AddressGridAggregationsResponse | null,
    currentZoom?: number
): void => {
    // Wait for style to load if needed
    if (!map.isStyleLoaded()) {
        map.once("style.load", () => {
            updateAddressAggregationsLayer(map, data, currentZoom);
        });
        return;
    }

    // Ensure source and layers exist
    ensureAddressAggregationsLayer(map);

    // Update source data
    const source = map.getSource(SOURCE_ID);
    if (source && source.type === "geojson") {
        const geoJsonSource = source as mapboxgl.GeoJSONSource;
        if (data && data.features.length > 0) {
            // Filter features by precision based on current zoom
            // Show only aggregations with precision <= max precision for zoom level
            // This ensures we hide small aggregations when zoomed out
            let filteredData = data;

            if (currentZoom !== undefined) {
                // Show only one precision at a time to avoid overlapping/doubled aggregations
                const targetPrecision = getVisiblePrecisionForZoom(currentZoom);

                // Log precision distribution for debugging (before filtering)
                const precisionCounts = data.features.reduce((acc, f) => {
                    const p = f.properties.geohashPrecision;
                    if (p !== undefined) {
                        acc[p] = (acc[p] || 0) + 1;
                    }
                    return acc;
                }, {} as Record<number, number>);

                // Filter to show only the target precision, but if it doesn't exist, show the closest lower precision
                const availablePrecisions = Object.keys(precisionCounts).map(Number).sort((a, b) => b - a);
                const selectedPrecision = availablePrecisions.find(p => p <= targetPrecision) ?? availablePrecisions[availablePrecisions.length - 1];

                const filteredFeatures = data.features.filter(feature => {
                    const precision = feature.properties.geohashPrecision;
                    if (precision === undefined) {
                        console.warn("[AddressAggregationsLayer] Feature missing geohashPrecision:", feature);
                        return false;
                    }
                    // Only show the selected precision to avoid overlapping
                    return precision === selectedPrecision;
                });

                filteredData = {
                    ...data,
                    features: filteredFeatures
                };

                console.log(
                    "[AddressAggregationsLayer] Filtered aggregations:",
                    data.features.length,
                    "->",
                    filteredFeatures.length,
                    `(zoom: ${currentZoom.toFixed(2)}, target precision: ${targetPrecision}, showing: ${selectedPrecision})`,
                    "Available precisions:",
                    availablePrecisions.join(", "),
                    "Counts:",
                    availablePrecisions.map(p => `${p}:${precisionCounts[p]}`).join(", ")
                );
            } else {
                console.log("[AddressAggregationsLayer] Updating with", data.features.length, "aggregation features (no zoom filter)");
            }

            geoJsonSource.setData(filteredData as any);
        } else {
            console.log("[AddressAggregationsLayer] No aggregation data, clearing layer");
            geoJsonSource.setData({ type: "FeatureCollection", features: [] } as any);
        }
    } else {
        console.warn("[AddressAggregationsLayer] Source not found or wrong type");
    }
};

/**
 * Removes the address aggregations layers from the map
 */
export const removeAddressAggregationsLayer = (map: MapboxMap): void => {
    try {
        if (map.getLayer(GRID_LABELS_LAYER_ID)) {
            map.removeLayer(GRID_LABELS_LAYER_ID);
        }
        if (map.getLayer(HEATMAP_LAYER_ID)) {
            map.removeLayer(HEATMAP_LAYER_ID);
        }
        if (map.getLayer("address-aggregations-circles")) {
            map.removeLayer("address-aggregations-circles");
        }
        if (map.getLayer("address-aggregations-fill")) {
            map.removeLayer("address-aggregations-fill");
        }
        if (map.getSource(SOURCE_ID)) {
            map.removeSource(SOURCE_ID);
        }
    } catch (error) {
        console.warn("[AddressAggregationsLayer] Failed to remove layer:", error);
    }
};